import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { recordVera } from "../lib/behavior";
import { COLOR_BY_FIRST, Icon, Orb, useStudio } from "../components/ui";
import { Composer } from "../components/Composer";
import { devMode } from "../auth/auth";
import { MeetingDialog } from "../components/Meeting";
import { cleanError, graph, mailFromGoogle, openLink } from "../lib/m365";
import { Markdown } from "../components/Markdown";
import { VoiceMode } from "../components/VoiceMode";
import { readFiles } from "../lib/attachments";
import { resolveFileRefs } from "../lib/m365Tools";
import type { ToolId } from "../lib/tools";
import { TodayCal, TodayDeadlines, TodayMail, TodayTeams } from "../components/Today";
import { plannedSteps, vera, type Attachment, type Card, type ChatMessage, type VeraAction } from "../api/vera";
import { ActionCard } from "../components/ActionCard";
import { ReadAloud } from "../components/ReadAloud";
import { CopyButton } from "../components/CopyButton";
import { CONVERSATIONS, SCOPE_COLOR, SCOPE_LABEL } from "../data/sample";
import { EDUTASK_TEMPLATES, sampleArtifact, TEMPLATES, type Artifact, type ArtifactKind, type WorkTemplate } from "../lib/artifacts";
import { openFile, saveFile, toWorkbook, XLSX_MIME } from "../lib/excel";
import { artifactToSpec, fileName as docFileName, generate, MIME, type FileFormat } from "../lib/docgen";
import { desktop } from "../lib/desktop";
import { getConvo, listConvos, saveConvo, serverHistory, type ConvoMeta } from "../lib/history";
import { L, t, type Lang, T, Tf } from "../lib/i18n";
import { CONTROL_PLANE_WORKSPACE, type ScopeId } from "../config/model";

const SCOPE_ABOUT: Record<ScopeId, { name: string; hello: string; about: string; ideas: string[] }> = {
  all: { name: "All of VERANeural", hello: "What would you like to work on?", about: "VERA uses everything your role can see, across every workspace.", ideas: ["What should I focus on this week?", "Summarise where every platform stands", "What's slipping, and who can help?"] },
  consumer: { name: "Consumer", hello: "VERA Consumer", about: "VERA uses the Consumer app's projects, release plan, reviews and files.", ideas: ["What's left before the App Store submission?", "Draft the App Store reviewer notes", "Create a RACI for the Consumer 2.0 launch"] },
  vds: { name: "VDS", hello: "VDS", about: "VERA for construction teams: My Day, Workspace and Operations.", ideas: ["Draft the VDS product brief", "Plan a VDS pilot with a construction company", "Make a timetable for the first VDS milestone"] },
  marine: { name: "Marine Intelligence", hello: "Marine Intelligence", about: "VERA uses Marine's jobs, Service Fusion status, files and people, including Carolyn.", ideas: ["What's blocking Marine right now?", "Draft a friendly note to Carolyn about credentials", "Make a priority matrix for Marine"] },
  // Security companies (the client pack DAMA resells): neutral, no company or people's names.
  dama: { name: "Your workspace", hello: "Your workspace", about: "VERA uses your company's records and files: shifts, time records, checkpoints, issues, incidents and invoices.", ideas: ["Who is on duty right now?", "Which issues are still open?", "Draft this month's hour report"] },
  aecch: { name: "Your workspace", hello: "Your workspace", about: "VERA uses your company's records and files: projects, budget, extras, invoices, tasks, technical questions, defects, site reports and safety.", ideas: ["What needs my decision today?", "Which extras are still in review, and what do they add up to?", "Which coordination issues block the site this week?", "Draft this week's owner report for the main project"] },
  aecus: { name: "Your workspace", hello: "Your workspace", about: "VERA uses your company's records and files: projects, budget, change orders, invoices, tasks, RFIs, punch list, daily reports and safety.", ideas: ["What needs my decision today?", "Which change orders are still in review, and what do they add up to?", "Which coordination issues block the site this week?", "Draft this week's owner report for the main project"] },
  veraai: { name: "VERA Core", hello: "VERA Core", about: "VERA uses VERA's models, training queue, evaluations and the Partner API.", ideas: ["How is VERA Core performing this week?", "What's in Monika's review queue?", "What's left before the Partner API goes live?"] },
  edutask: { name: "EduTask", hello: "EduTask", about: "VERA works with EduTask on funding and investment support, quality assurance and evaluation, research, training, curriculum and qualification design, and digital transformation. Every deliverable downloads as Word, PDF or Excel.", ideas: ["Draft an EU funding proposal for a client SME, with a budget", "Outline a self-evaluation report for programme accreditation with the Kosovo Accreditation Agency", "Design learning outcomes and modules for a level 5 qualification", "Plan a digital maturity assessment for a municipality"] },
  investor: { name: "Investor updates", hello: "Investor updates", about: "VERA uses the investor updates, milestones and metrics shared with investors.", ideas: ["Summarise the Q4 investor update", "What changed since the last update?", "Draft questions for the next investor call"] },
};

/** EduTask's workspace in Albanian (standard Albanian, as used in Kosovo and Albania). */
const SCOPE_ABOUT_SQ: Partial<Record<ScopeId, { name: string; hello: string; about: string; ideas: string[] }>> = {
  edutask: { name: "EduTask", hello: "EduTask", about: "VERA punon me EduTask-un në mbështetjen për financim dhe investime, sigurimin e cilësisë dhe vlerësimin, kërkimin, trajnimin, hartimin e kurrikulave dhe kualifikimeve, si dhe transformimin digjital. Çdo dokument shkarkohet si Word, PDF ose Excel.", ideas: ["Harto një propozim për financim nga BE-ja për një NVM klient, me buxhet", "Përgatit strukturën e raportit të vetëvlerësimit për akreditimin e programit në Agjencinë e Kosovës për Akreditim", "Harto rezultatet e të nxënit dhe modulet për një kualifikim të nivelit 5", "Planifiko një vlerësim të pjekurisë digjitale për një komunë"] },
};
const aboutFor = (scope: ScopeId, lang: Lang) => {
  if (lang === "sq" && SCOPE_ABOUT_SQ[scope]) return SCOPE_ABOUT_SQ[scope]!;
  const a = SCOPE_ABOUT[scope];
  // German comes from i18n.de.ts by the English, like every other text.
  return lang === "de" && a ? { ...a, name: T(a.name), hello: T(a.hello), about: T(a.about), ideas: a.ideas.map(T) } : a;
};

/**
 * What an empty conversation offers. A company's own people (Marine, a partner, a pack) get a short start in their own
 * words: "Ready when you are", and three one-tap starts (never the team's view of their company). EduTask and the
 * VERANeural team keep theirs.
 */
const startFor = (scope: ScopeId, lang: Lang, profile: { external: boolean; crm?: boolean; key: string }, name: string) => {
  const a = aboutFor(scope, lang);
  if (!profile.external || !profile.crm || profile.key === "client-edutask") return a;
  const first = name.split(" ")[0];
  const marine = profile.key === "client-marine" || scope === "marine";
  return {
    ...a,
    hello: first ? Tf("Ready when you are, {name}.", { name: first }) : T("Ready when you are."),
    about: "",
    ideas: marine ? [T("Check my email"), T("Add a job"), T("What's on today?")] : [T("Check my email"), T("What's on today?"), T("What needs me?")],
  };
};

type Convo = { id: string; title: string; scope: ScopeId; group: string; updated: number };
type Store = { convos: Convo[]; messages: Record<string, ChatMessage[]> };

const SEED_MESSAGES: Record<string, ChatMessage[]> = {
  c1: [
    { role: "user", text: "What should I focus on before the App Store submission?" },
    { role: "vera", text: "Three things, in order. First, PR #142 has one failing voice test, so the build isn't ready yet. Second, the privacy labels in App Store Connect need to match the new Privacy Policy. Third, the old onboarding text goes away with the new welcome screen.", specialists: ["Projects", "Engineering", "Documents"] },
  ],
};

function loadStore(userId: string, sample: boolean, scopes: ScopeId[]): Store {
  try {
    const raw = localStorage.getItem(`vera-ws-${userId}`);
    if (raw) {
      const s = JSON.parse(raw) as Store;
      if (sample) return s;
      // Real accounts never keep the demo's example conversations (ids c1 to c99).
      const keep = s.convos.filter((c) => !/^c\d{1,2}$/.test(c.id));
      return { convos: keep, messages: Object.fromEntries(keep.map((c) => [c.id, s.messages[c.id] ?? []])) };
    }
  } catch { /* ignore */ }
  if (!sample) return { convos: [], messages: {} };
  const now = Date.now();
  const convos = CONVERSATIONS.filter((c) => scopes.includes(c.scope)).map((c, i) => ({ ...c, updated: now - i * 3600_000 }));
  return { convos, messages: Object.fromEntries(convos.map((c) => [c.id, SEED_MESSAGES[c.id] ?? []])) };
}

export function Workspace() {
  const { user, profile, pending, clearPending, toast, getToken, consent, openConsent, m365, go, lang } = useStudio();
  const [meeting, setMeeting] = useState(false);
  const scopes = profile.scopes;
  // Live, VERA can answer only in workspaces the control plane has (not DAMA or VERA Core yet), so only those can be chosen.
  const usable = (s: ScopeId) => scopes.includes(s) && (vera.mode !== "live" || Boolean(CONTROL_PLANE_WORKSPACE[s]));
  const [store, setStore] = useState<Store>(() => loadStore(user.id, devMode, scopes));
  const [active, setActive] = useState<string | null>(() => store.convos[0]?.id ?? null);
  const [scope, setScope] = useState<ScopeId>(() => (usable("all") ? "all" : [store.convos[0]?.scope, ...scopes].find((s): s is ScopeId => Boolean(s) && usable(s as ScopeId)) ?? scopes[0]));
  const [q, setQ] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<"create" | "today">("create");
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showHist, setShowHist] = useState(false);
  const [tools, setTools] = useState<ToolId[]>([]);
  const [voice, setVoice] = useState(false);
  const [focus, setFocusState] = useState(() => { try { return localStorage.getItem("vera-focus") === "1"; } catch { return false; } });
  const setFocus = (f: (v: boolean) => boolean) => setFocusState((v) => { const n = f(v); try { localStorage.setItem("vera-focus", n ? "1" : "0"); } catch { /* ignore */ } return n; });
  const [dragging, setDragging] = useState(false);
  // The Create panel starts hidden so the conversation has the room; one click shows it.
  const [side, setSideState] = useState(() => { try { return localStorage.getItem("vera-side2") === "1"; } catch { return false; } });
  const toggleSide = () => setSideState((v) => { try { localStorage.setItem("vera-side2", v ? "0" : "1"); } catch { /* ignore */ } return !v; });
  // Conversations are always saved; the list stays out of the way until the person opens it (the history button by the chips).
  const [hist, setHist] = useState(() => { try { return localStorage.getItem("vera-hist") === "1"; } catch { return false; } });
  const toggleHist = () => setHist((v) => { try { localStorage.setItem("vera-hist", v ? "0" : "1"); } catch { /* ignore */ } return !v; });
  const [steps, setSteps] = useState<string[]>([]);
  const [stepAt, setStepAt] = useState(0);
  const msgsRef = useRef<HTMLDivElement>(null);
  const dragDepth = useRef(0);
  // Files the person gave VERA in a turn, kept in memory only (never saved), so an email card can attach them.
  const actionFiles = useRef(new Map<string, Attachment[]>());
  // PDFs attached in this session, by conversation, so "now change the date in it" still has the original file to edit.
  const sessionPdfs = useRef(new Map<string, Attachment[]>());

  // Walk through VERA's steps while she works.
  useEffect(() => {
    if (!busy) return;
    setStepAt(0);
    const t = window.setInterval(() => setStepAt((i) => Math.min(i + 1, Math.max(0, steps.length - 1))), 650);
    return () => window.clearInterval(t);
  }, [busy, steps.length]);

  useEffect(() => { try { localStorage.setItem(`vera-ws-${user.id}`, JSON.stringify(store)); } catch { /* ignore */ } }, [store, user.id]);

  // ---------- history on VERA's server (see lib/history.ts), so it's the same on the web, the desktop and any computer ----------
  const sync = serverHistory();
  const storeRef = useRef(store);
  storeRef.current = store;
  /** Conversations changed here that still need saving to the server. */
  const dirty = useRef(new Set<string>());
  /** Conversations whose full history is on the server and not loaded here yet. Never saved until loaded. */
  const partial = useRef(new Set<string>());
  const [loadingConvo, setLoadingConvo] = useState<string | null>(null);
  const changed = (id: string) => { if (sync) dirty.current.add(id); };

  // After sign-in: the server's list, merged with this device's copy. Conversations only on this device go up once.
  useEffect(() => {
    if (!sync) return;
    let off = false;
    void (async () => {
      let list: ConvoMeta[];
      try { list = await listConvos(); } catch { return; /* this device's copy stays, and saves resume next time */ }
      if (off) return;
      const local = storeRef.current;
      const server = new Map(list.map((c) => [c.id, c]));
      for (const c of local.convos) {
        const sv = server.get(c.id);
        if ((local.messages[c.id] ?? []).length && (!sv || c.updated > sv.updated)) dirty.current.add(c.id);
      }
      setStore((s) => {
        const byId = new Map(s.convos.map((c) => [c.id, c]));
        const messages = { ...s.messages };
        for (const sv of list) {
          const mine = byId.get(sv.id);
          if (mine && sv.updated <= mine.updated && messages[sv.id]) continue;
          byId.set(sv.id, { id: sv.id, title: sv.title, scope: sv.scope as ScopeId, group: sv.group || "Today", updated: sv.updated });
          delete messages[sv.id];
          partial.current.add(sv.id);
        }
        return { convos: [...byId.values()].sort((a, b) => b.updated - a.updated), messages };
      });
    })();
    return () => { off = true; };
  }, [sync, user.id]);

  /** Loads a conversation's history from the server, once at a time. Anything said here meanwhile comes after it. */
  const loading = useRef(new Map<string, Promise<void>>());
  function loadConvo(id: string): Promise<void> {
    if (!partial.current.has(id)) return Promise.resolve();
    const running = loading.current.get(id);
    if (running) return running;
    setLoadingConvo(id);
    const p = (async () => {
      try {
        const c = await getConvo(id);
        partial.current.delete(id);
        const added = storeRef.current.messages[id] ?? [];
        const next = { ...storeRef.current, messages: { ...storeRef.current.messages, [id]: [...(c.messages ?? []), ...added] } };
        storeRef.current = next;
        setStore(next);
        if (added.length) changed(id);
      } finally {
        loading.current.delete(id);
        setLoadingConvo((x) => (x === id ? null : x));
      }
    })();
    loading.current.set(id, p);
    return p;
  }
  // The open conversation loads when it's opened, and right after the server's list arrives.
  useEffect(() => {
    if (sync && active && partial.current.has(active)) void loadConvo(active).catch(() => toast(L("Couldn't load this conversation. Please check your connection.", "Kjo bisedë nuk u ngarkua. Të lutem kontrollo lidhjen.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sync, store.convos]);

  // Changes go to the server a moment after they happen.
  useEffect(() => {
    if (!sync || !dirty.current.size) return;
    const t = window.setTimeout(() => {
      const s = storeRef.current;
      for (const id of [...dirty.current]) {
        if (partial.current.has(id)) continue; // saved once its history has loaded
        dirty.current.delete(id);
        const c = s.convos.find((x) => x.id === id);
        const msgs = s.messages[id] ?? [];
        if (!c || !msgs.length) continue; // an empty new conversation is saved once something is said
        void saveConvo(c, msgs).catch(() => { dirty.current.add(id); });
      }
    }, 800);
    return () => window.clearTimeout(t);
  }, [store, sync]);

  const messages = active ? store.messages[active] ?? [] : [];
  // Follow the conversation to its end; an empty one stays at the top so its welcome and suggestions are fully visible.
  useEffect(() => { const el = msgsRef.current; if (el) el.scrollTo({ top: messages.length || busy ? el.scrollHeight : 0, behavior: messages.length ? "smooth" : "auto" }); }, [messages.length, busy, stepAt]);

  // Requests arriving from other areas (Home priorities, Files "Ask VERA", Projects). Each one is sent once, even when
  // React runs this twice (development) or the page re-renders before clearPending lands.
  const handled = useRef<unknown>(null);
  useEffect(() => {
    if (!pending || handled.current === pending) return;
    handled.current = pending;
    if (pending.project && usable(pending.project)) setScope(pending.project);
    const files = [...(pending.attach ? [pending.attach] : []), ...(pending.files ?? [])];
    if (pending.ask) void send(pending.ask, pending.project, files, pending.fresh, pending.title);
    else if (files.length) setAttachments((a) => [...a, ...files]);
    clearPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const groups = useMemo(() => {
    const day = 24 * 3600_000, now = Date.now();
    const shown = store.convos.filter((c) => scopes.includes(c.scope) && (scope === "all" || c.scope === scope) && (!q || c.title.toLowerCase().includes(q.toLowerCase())));
    const order = ["Pinned", "Today", "This week", "Earlier"];
    const label = (c: Convo) => (c.group === "Pinned" ? "Pinned" : now - c.updated < day ? "Today" : now - c.updated < 7 * day ? "This week" : "Earlier");
    return order.map((g) => [g, shown.filter((c) => label(c) === g)] as const).filter(([, list]) => list.length);
  }, [store.convos, q, scopes, scope]);

  function newConversation(title = "New conversation", forScope: ScopeId = scope) {
    const id = `c${Date.now()}`;
    setStore((s) => ({ convos: [{ id, title, scope: forScope, group: "Today", updated: Date.now() }, ...s.convos], messages: { ...s.messages, [id]: [] } }));
    changed(id);
    setActive(id);
    setArtifact(null);
    return id;
  }

  function push(id: string, ...msgs: ChatMessage[]) {
    setStore((s) => ({
      convos: s.convos.map((c) => (c.id === id ? { ...c, updated: Date.now(), title: c.title === "New conversation" && msgs[0]?.role === "user" ? msgs[0].text.slice(0, 48) : c.title } : c)),
      messages: { ...s.messages, [id]: [...(s.messages[id] ?? []), ...msgs] },
    }));
    changed(id);
  }

  /** What this person has connected, for VERA to look up herself, in the chat and by voice. */
  function lookups(): string[] {
    return [
      ...(m365.mail() ? ["mail", "calendar", ...(desktop?.web || !desktop ? ["links"] : [])] : []),
      ...(m365.connected("planner") ? ["planner"] : []),
      ...(m365.connected("teams") ? ["teams"] : []),
      ...(m365.connected("files") ? ["files"] : []),
      ...(desktop && profile.areas.includes("operations") ? ["azure"] : []),
    ];
  }

  async function send(raw: string, forScope?: ScopeId, withFiles?: Attachment[], fresh = false, title?: string) {
    const files = withFiles ?? attachments;
    const t = raw.trim() || (files.length ? (files.length === 1 ? L(`Take a look at ${files[0].name}`, `Shiko ${files[0].name}`) : L("Take a look at these files", "Shiko këta skedarë")) : "");
    if (!t || busy) return;
    const current = store.convos.find((c) => c.id === active);
    // VERA needs the earlier messages of this conversation, so its history loads first.
    if (current && partial.current.has(current.id)) {
      try { await loadConvo(current.id); } catch { toast(L("Couldn't load this conversation. Please check your connection.", "Kjo bisedë nuk u ngarkua. Të lutem kontrollo lidhjen.")); return; }
    }
    // An older conversation in a workspace this person can no longer use (JLB, or "All" for the team) continues in a new one.
    const useScope = forScope && usable(forScope) ? forScope : current && !fresh && usable(current.scope) ? current.scope : scope;
    const id = !fresh && current && current.scope === useScope ? current.id : newConversation(title ?? "New conversation", useScope);
    const prior = id === current?.id ? storeRef.current.messages[id] ?? [] : [];
    const isPdf = (f: Attachment) => Boolean(f.dataUrl) && (/\.pdf$/i.test(f.name) || f.mime === "application/pdf");
    if (files.some(isPdf)) sessionPdfs.current.set(id, [...(sessionPdfs.current.get(id) ?? []).filter((x) => !files.some((y) => y.name === x.name)), ...files.filter(isPdf)].slice(-3));
    // Asking for a change to a PDF from earlier in this conversation: send that PDF again, so VERA reads its exact words.
    const earlier = sessionPdfs.current.get(id) ?? [];
    const wantsEdit = /\b(edit|change|replace|fix|correct|update|fill|delete page|rotate)\b|ndrysho|ndryshim|zëvendëso|korrigjo|përditëso|plotëso|fshi faqe|rrotullo/i;
    const sendFiles = !files.some(isPdf) && earlier.length && wantsEdit.test(t) ? [...files, earlier[earlier.length - 1]] : files;
    const useTools = tools;
    push(id, { role: "user", text: t, at: Date.now(), tools: useTools.length ? useTools : undefined,
      files: files.length ? files.map((f) => ({ name: f.name, kind: f.kind ?? "text", thumb: f.thumb, size: f.size })) : undefined });
    setText("");
    setAttachments([]);
    setSteps(plannedSteps(t, files, useTools));
    setBusy(true);
    try {
      const r = await vera.ask({
        text: t, scope: useScope, attachments: sendFiles, tools: useTools,
        history: prior.filter((m) => m.text).slice(-10).map((m) => ({ role: m.role, text: m.text })),
        // VERA looks up whatever this person has connected, herself, with their own sign-in: email and calendar (and the links in
        // emails), Planner, Teams chats, SharePoint and OneDrive, and for the team, Azure. Everything she can reach only reads.
        // Files are always possible: VERA prepares the content and this app makes the Word, PDF, Excel or PowerPoint file.
        clientTools: [...lookups(), "documents", "pdfedit"],

        onStep: (step) => setSteps((st) => (st.includes(step) ? st : [...st, step])),
      }, getToken);
      for (const a of r.actions ?? []) {
        // Files VERA looked up (SharePoint/OneDrive, an email attachment) that she wants to attach: resolve them to real files here.
        if (a.email?.attachRefs?.length) {
          const resolved = await resolveFileRefs(a.email.attachRefs);
          const asFiles: Attachment[] = resolved.files.map((f) => ({ name: f.name, text: "", kind: "document", mime: f.mime, size: f.size, dataUrl: f.dataUrl }));
          if (asFiles.length) { actionFiles.current.set(a.id, [...(actionFiles.current.get(a.id) ?? []), ...asFiles]); a.email.attachFiles = true; }
          if (resolved.links.length) a.email.body = `${a.email.body}\n\n${resolved.links.map((l) => `${l.name}: ${l.url}`).join("\n")}`;
          if (resolved.failed.length) toast(L(`Couldn't attach: ${resolved.failed.join("; ")}`, `Nuk u bashkëngjit: ${resolved.failed.join("; ")}`));
        }
        if (a.email?.attachFiles && files.length) actionFiles.current.set(a.id, [...(actionFiles.current.get(a.id) ?? []), ...files]);
        if (a.type === "pdfedit") actionFiles.current.set(a.id, [...sendFiles, ...earlier]);
      }
      push(id, { role: "vera", text: r.text, at: Date.now(), specialists: r.specialists, steps: r.steps, sources: r.sources, cards: r.cards, sample: r.sample, proposals: r.proposals, artifact: r.artifact, slots: r.slots, actions: r.actions });
      if (r.artifact) { setArtifact(sampleArtifact(r.artifact)); setSideState(true); }
    } catch (e) {
      push(id, { role: "vera", text: e instanceof Error ? e.message : L("Something went wrong.", "Diçka shkoi keq.") });
    } finally {
      setBusy(false);
    }
  }

  /** Remember what happened to an email or meeting card, so it can't be sent or booked twice. */
  function markAction(convo: string, index: number, actionId: string, done: NonNullable<VeraAction["done"]>) {
    setStore((s) => ({ ...s, messages: { ...s.messages, [convo]: (s.messages[convo] ?? []).map((m, j) => (j === index ? { ...m, actions: m.actions?.map((a) => (a.id === actionId ? { ...a, done } : a)) } : m)) } }));
    changed(convo);
  }

  // EduTask's own documents: VERA writes them as a downloadable file, in the language of the screen.
  function createWork(w: WorkTemplate) {
    const asFile = w.format === "Excel" ? L(" as an Excel file", " si skedar Excel") : L(" as a document I can download as Word or PDF", " si dokument që mund ta shkarkoj si Word ose PDF");
    void send(L(w.prompt[0], w.prompt[1]) + (vera.mode === "live" ? asFile : ""));
  }

  function create(kind: ArtifactKind) {
    if (kind === "meet" && m365.mail()) { setMeeting(true); return; }
    if (kind === "planner" && m365.connected("planner")) { go("planner"); return; }
    const tpl = TEMPLATES.find((x) => x.id === kind)!;
    // Live: VERA builds the real thing from what she knows and returns a downloadable file. Sample mode keeps the templates.
    const asFile = tpl.format === "Excel" ? " as an Excel file" : tpl.format === "Word" ? " as a document I can download as Word or PDF" : "";
    void send(vera.mode === "live" && asFile ? `${tpl.prompt}${asFile}` : tpl.prompt);
  }

  async function downloadExcel(a: Artifact) {
    try {
      const bytes = await toWorkbook(a);
      const where = await saveFile(`${a.title.replace(/[^\w\s.-]/g, "").replace(/\s+/g, " ").trim()}.xlsx`, bytes, XLSX_MIME);
      if (where) toast(desktop ? Tf("Saved to {where}", { where }) : T("Excel workbook downloaded"));
    } catch {
      toast(T("Couldn't create the Excel file. Please try again."));
    }
  }

  function onDragEnter(e: DragEvent) { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); dragDepth.current++; setDragging(true); } }
  function onDragLeave() { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); }
  async function onDrop(e: DragEvent) {
    e.preventDefault(); dragDepth.current = 0; setDragging(false);
    const r = await readFiles(e.dataTransfer.files, attachments.length);
    if (r.files.length) setAttachments((a) => [...a, ...r.files]);
    if (r.skipped.length) toast(L(`Not added: ${r.skipped.join("; ")}`, `Nuk u shtuan: ${r.skipped.join("; ")}`));
    document.getElementById("say")?.focus();
  }

  function endVoice(all: { who: "you" | "vera"; text: string }[]) {
    setVoice(false);
    // Speech that wasn't transcribed arrives as empty lines; they're not worth keeping.
    const lines = all.filter((l) => l.text.trim());
    if (!lines.length) return;
    const id = active && store.convos.some((c) => c.id === active) ? active : newConversation("Voice conversation");
    push(id, ...lines.map((l) => ({ role: l.who === "you" ? "user" as const : "vera" as const, text: l.text, at: Date.now() })));
  }
  // Live, a workspace chip appears only when VERA has that workspace (DAMA has none yet), so no chip leads to an error.
  const chips = scopes.filter(usable);
  function openScope(id: ScopeId) {
    setScope(id);
    setArtifact(null);
    const latest = store.convos.filter((c) => id === "all" || c.scope === id).sort((a, b) => b.updated - a.updated)[0];
    setActive(latest?.id ?? null);
  }

  const activeConvo = store.convos.find((c) => c.id === active);
  const viewScope = activeConvo?.scope ?? scope;

  return (
    <>
      <div className={`ws ${showHist ? "showhist" : ""} ${focus ? "focus" : ""} ${side ? "" : "noside"} ${hist ? "" : "nohist"}`}>
        <aside className="card hist" aria-label={L("Conversation history", "Historiku i bisedave")}>
          <label className="search" htmlFor="hq"><Icon name="search" size={14} /><input id="hq" placeholder={t(lang, "ws.search")} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" /></label>
          <div className="hist-list">
            {groups.length ? groups.map(([g, list]) => (
              <div key={g}>
                <div className="hist-group">{L(g, ({ Pinned: "Të fiksuara", Today: "Sot", "This week": "Këtë javë", Earlier: "Më herët" } as Record<string, string>)[g] ?? g)}</div>
                {list.map((c) => (
                  <button key={c.id} className={`hist-item ${c.id === active ? "on" : ""}`} onClick={() => { setActive(c.id); setArtifact(null); setShowHist(false); }}>
                    <span className="dot" style={{ background: SCOPE_COLOR[c.scope] }} /><span>{c.title === "New conversation" ? L(c.title, "Bisedë e re") : c.title === "Voice conversation" ? L(c.title, "Bisedë me zë") : c.title}</span>
                  </button>))}
              </div>)) : <p className="faint" style={{ padding: 8 }}>{q ? L("No conversations match.", "Asnjë bisedë nuk përputhet.") : L("Your conversations will appear here.", "Bisedat e tua do të shfaqen këtu.")}</p>}
          </div>
          <p className="faint" style={{ fontSize: 11.5 }}>{L("Kept privately for you. Shared only if you choose.", "Ruhen privatisht për ty. Ndahen vetëm nëse zgjedh ti.")}</p>
        </aside>

        <section className={`card convo ${dragging ? "dragging" : ""}`} aria-label={L("Conversation with VERA", "Biseda me VERA-n")}
          onDragEnter={onDragEnter} onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) e.preventDefault(); }} onDragLeave={onDragLeave} onDrop={(e) => void onDrop(e)}>
          {dragging && <div className="dropzone"><Orb size={44} mode="breathe" /><b>{L("Drop files or images for VERA", "Lësho skedarë ose imazhe për VERA-n")}</b><span className="faint">{L("Text, code, PDFs, Word, Excel, PowerPoint and images", "Tekst, kod, PDF, Word, Excel, PowerPoint dhe imazhe")}</span></div>}
          {meeting && <MeetingDialog onClose={() => setMeeting(false)} />}
          {voice && <VoiceMode scope={scope} getToken={getToken} onClose={endVoice} lookups={lookups()} />}
          <div className="convo-head">
            <button className="iconbtn ghost histbtn" onClick={() => setShowHist((v) => !v)} aria-label={L("Conversation history", "Historiku i bisedave")} title={L("Conversation history", "Historiku i bisedave")}><Icon name="history" size={17} /></button>
            <div className="chips">{chips.map((id) => <button key={id} className={`chip ${scope === id ? "on" : ""}`} aria-pressed={scope === id} onClick={() => openScope(id)}><span className="dot" style={{ background: SCOPE_COLOR[id] }} />{SCOPE_LABEL[id]}</button>)}</div>
            {!focus && <button className={`iconbtn ghost histtoggle${hist ? " on" : ""}`} onClick={toggleHist} aria-pressed={hist} aria-label={hist ? L("Hide your conversations", "Fshih bisedat") : L("Show your conversations", "Shfaq bisedat")} title={hist ? L("Hide your conversations", "Fshih bisedat") : L("Your conversations (always saved)", "Bisedat e tua (ruhen gjithmonë)")}><Icon name="history" size={17} /></button>}
            {!focus && <button className="iconbtn ghost" onClick={toggleSide} aria-pressed={!side} aria-label={side ? L("Hide the Create panel", "Fshih panelin Krijo") : L("Show the Create panel", "Shfaq panelin Krijo")} title={side ? L("Hide the Create panel", "Fshih panelin Krijo") : L("Show the Create panel", "Shfaq panelin Krijo")}><Icon name="panel" size={17} /></button>}
            <button className="iconbtn ghost" onClick={() => setFocus((f) => !f)} aria-pressed={focus} aria-label={focus ? L("Show side panels", "Shfaq panelet anësore") : L("Focus on the conversation", "Përqendrohu te biseda")} title={focus ? L("Show side panels", "Shfaq panelet anësore") : L("Focus mode", "Pamja e përqendruar")}><Icon name={focus ? "shrink" : "expand"} size={17} /></button>
            <button className="btn primary newconvo" onClick={() => newConversation()} title={L("New conversation", "Bisedë e re")} aria-label={L("New conversation", "Bisedë e re")}><Icon name="plus" size={16} /></button>
          </div>
          <div className="msgs" ref={msgsRef}>
            {loadingConvo === active && active && (
              <div className="stack" style={{ margin: "auto", textAlign: "center", alignItems: "center", gap: 10 }}>
                <Orb mode="think" size={40} /><p className="mute">{L("Loading this conversation…", "Po ngarkohet biseda…")}</p>
              </div>
            )}
            {!messages.length && !(loadingConvo === active && active) && (
              <div className="stack ws-hello" style={{ margin: "auto", textAlign: "center", alignItems: "center", maxWidth: 520, gap: 12 }}>
                {(() => {
                  const a = startFor(viewScope, lang, profile, user.name);
                  return <>
                    <Orb mode="breathe" size={56} /><h2>{activeConvo?.title && activeConvo.title !== "New conversation" ? activeConvo.title : a.hello}</h2>
                    {a.about && <p className="mute">{a.about}</p>}
                    <div className="suggest">{a.ideas.map((s) => <button key={s} className="btn" onClick={() => void send(s, viewScope)}>{s}</button>)}</div>
                  </>;
                })()}
              </div>
            )}
            {messages.map((m, i) => m.role === "user" && !m.text.trim() && !m.files?.length ? null : m.role === "user"
              ? (
                <div className="msg me" key={i}>
                  {!!m.files?.length && <div className="mfiles">{m.files.map((f, j) => f.kind === "image" && f.thumb
                    ? <img key={j} src={f.thumb} alt={f.name} title={f.name} />
                    : <span key={j} className="mfile"><Icon name={f.kind === "document" ? "docai" : "doc"} size={13} />{f.name}</span>)}</div>}
                  <div>{m.text}</div>

                </div>)
              : (
                <div className="msg vera" key={i}>
                  <div className="who"><Orb size={12} />VERA{m.sample && <span className="sampletag">{L("Sample", "Shembull")}</span>}</div>
                  <Markdown text={m.text} />
                  {m.cards?.map((c, j) => <ReplyCard key={j} c={c} />)}
                  {!!m.sources?.length && <div className="sources"><span className="faint">{L("Sources", "Burimet")}</span>{m.sources.map((s, j) => <a key={j} href={s.url} target="_blank" rel="noreferrer noopener"><span className="sn">{j + 1}</span>{s.title}</a>)}</div>}
                  {m.artifact && <div className="proposal"><b>{TEMPLATES.find((t) => t.id === m.artifact)?.title}</b> {L("is ready.", "është gati.")}<div className="row" style={{ marginTop: 8 }}><button className="btn primary" onClick={() => setArtifact(sampleArtifact(m.artifact!))}>{L("Open", "Hap")}</button></div></div>}
                  {m.slots && <div className="proposal"><b>{L("Proposed times", "Oraret e propozuara")}</b><div className="row" style={{ marginTop: 8 }}>{m.slots.map((s) => <button key={s} className="btn" onClick={() => push(active!, { role: "user", text: s }, { role: "vera", text: `Booked: ${s}. The Teams invite with an agenda is in everyone's calendar.`, specialists: ["Calendar", "Teams"] })}>{s}</button>)}</div></div>}
                  {!!m.text && <div className="row" style={{ gap: 10 }}><CopyButton text={m.text} />{vera.mode === "live" && <ReadAloud text={m.text} />}</div>}
                  {m.actions?.map((a) => <ActionCard key={a.id} action={a} files={actionFiles.current.get(a.id)} docs={messages.flatMap((x) => x.actions ?? []).filter((x) => x.type === "document" && x.document).map((x) => x.document!)} onDone={(d) => markAction(active!, i, a.id, d)} />)}
                  {m.proposals?.map((p) => (
                    <div className="proposal" key={p.id}><b>{L("VERA suggests:", "VERA sugjeron:")}</b> {p.description}</div>))}
                  {(!!m.specialists?.length || !!m.steps?.length) && (
                    <details className="how"><summary><span className="used">{(m.specialists ?? []).map((s) => <span key={s}>{s}</span>)}</span><span className="faint">{L("How VERA worked on this", "Si punoi VERA për këtë")}</span></summary>
                      <ol>{(m.steps ?? []).map((s, j) => <li key={j}><Icon name="check" size={12} /> {s}</li>)}</ol></details>)}
                </div>
              ))}
            {busy && (
              <div className="msg vera working"><div className="who"><Orb size={12} mode="think" />VERA</div>
                <ol className="steps">{steps.slice(0, stepAt + 1).map((s, j) => <li key={j} className={j === stepAt ? "now" : "done"}>{j === stepAt ? <span className="spin" /> : <Icon name="check" size={12} />} {s}</li>)}</ol>
              </div>)}
          </div>
          <Composer placeholder={lang === "sq" ? t(lang, "ws.composer") : viewScope === "all" || SCOPE_ABOUT[viewScope].name === "Your workspace" ? undefined : Tf("Ask VERA about {what}, type / for commands, or drop files here", { what: SCOPE_ABOUT[viewScope].name })} text={text} setText={setText} attachments={attachments} setAttachments={setAttachments} tools={tools} setTools={setTools}
            busy={busy} onSend={() => { recordVera("type"); void send(text); }} onVoice={() => { if (consent.mic) setVoice(true); else { toast(L("Turn on the microphone for voice in Privacy & consent first.", "Aktivizo më parë mikrofonin për zërin te Privatësia dhe pëlqimi.")); openConsent(); } }} />
          <div className="only"><Icon name="lock" size={13} /> {L("Only VERA. Everything runs through VERANeural's control plane, under your organisation's rules.", "Vetëm VERA. Gjithçka kalon përmes sistemit të sigurt të VERANeural, sipas rregullave të organizatës sate.")}</div>
        </section>

        <aside className="card canvasp" aria-label={L("Create and today", "Krijo dhe sot")}>
          {artifact ? <ArtifactView a={artifact} onClose={() => setArtifact(null)} onExcel={downloadExcel} onPlanner={() => void send("Turn the launch plan into Planner tasks")} /> : (
            <>
              <div className="tabs" role="tablist">
                <button role="tab" aria-selected={panel === "create"} className={panel === "create" ? "on" : ""} onClick={() => setPanel("create")}>{L("Create", "Krijo")}</button>
                <button role="tab" aria-selected={panel === "today"} className={panel === "today" ? "on" : ""} onClick={() => setPanel("today")}>{L("Today", "Sot")}</button>
              </div>
              <div className="cp-body">
                {panel === "create" ? (
                  <>
                    <p className="faint" style={{ fontSize: 12.5 }}>{L("VERA builds it from your projects, team and calendar. You review before anything is shared.", "VERA e ndërton nga projektet, ekipi dhe kalendari yt. Ti e rishikon para se të ndahet diçka.")}</p>
                    {profile.key === "client-edutask" ? EDUTASK_TEMPLATES.map((w) => (
                      <button className="tpl" key={w.id} onClick={() => createWork(w)}>
                        <span className="ico"><Icon name={w.icon} /></span>
                        <span><b>{L(w.title[0], w.title[1])}</b><small>{L(w.detail[0], w.detail[1])}</small></span>
                        <span className={`fmt ${w.format === "Excel" ? "x" : "w"}`}>{w.format}</span>
                      </button>)) : TEMPLATES.map((t) => (
                      <button className="tpl" key={t.id} onClick={() => create(t.id)}>
                        <span className="ico"><Icon name={({ raci: "grid", timetable: "cal", matrix: "matrix", status: "doc", meeting: "chat", meet: "teams", planner: "board", email: "mail" } as Record<string, string>)[t.id]} /></span>
                        <span><b>{t.title}</b><small>{t.detail}</small></span>
                        <span className={`fmt ${({ Excel: "x", Word: "w", Outlook: "o", Teams: "t", Planner: "p" } as Record<string, string>)[t.format]}`}>{t.format}</span>
                      </button>))}
                  </>
                ) : (
                  <>
                    <div className="row" style={{ justifyContent: "space-between" }}><b>{t(lang, "home.calendar")}</b><span className="src">{mailFromGoogle() ? T("Google Calendar") : m365.mail() ? "Outlook" : ""}</span></div><TodayCal />
                    <div className="row" style={{ justifyContent: "space-between", marginTop: 6 }}><b>{t(lang, "home.inbox")}</b><span className="src">{mailFromGoogle() ? "Gmail" : m365.mail() ? "Outlook" : ""}</span></div>
                    <TodayMail onDraft={(from) => void send(L(`Draft a friendly email reply to ${from}`, `Harto një përgjigje miqësore për ${from}`))} />
                    {profile.areas.includes("proposals") && !m365.connected("teams")
                      ? <><div className="row" style={{ justifyContent: "space-between", marginTop: 6 }}><b>{t(lang, "today.deadlines")}</b><span className="src">VERA</span></div><TodayDeadlines /></>
                      : <><div className="row" style={{ justifyContent: "space-between", marginTop: 6 }}><b>Teams</b><span className="src">{T("Microsoft Teams")}</span></div><TodayTeams /></>}
                  </>
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  );
}

function ArtifactView({ a, onClose, onExcel, onPlanner }: { a: Artifact; onClose: () => void; onExcel: (a: Artifact) => void; onPlanner: () => void }) {
  const { toast, m365 } = useStudio();
  async function downloadAs(art: Artifact, format: FileFormat, how: "open" | "save" = "open") {
    try {
      const spec = artifactToSpec(art);
      const name = docFileName(spec.title, format), bytes = await generate(spec, format);
      if (how === "open") await openFile(name, bytes, MIME[format]);
      else { const where = await saveFile(name, bytes, MIME[format]); if (where) toast(Tf("Saved {where}", { where })); }
    } catch (e) { toast(e instanceof Error ? e.message : L("That file couldn't be made.", "Ky skedar nuk u krijua.")); }
  }
  async function toDrafts(subject: string, body: string) {
    if (!m365.mail()) { toast(T("Connect Outlook in Connections, and VERA can save this in your Drafts.")); return; }
    try {
      const r = await graph<{ cancelled?: boolean; webLink?: string }>("mail.draft", { subject, body });
      if (!r.cancelled) { toast(T("Saved in your Outlook Drafts. Add recipients and send it when you're ready.")); openLink(r.webLink); }
    } catch (e) { toast(cleanError(e)); }
  }
  return (
    <>
      <div className="row" style={{ justifyContent: "space-between" }}><b>{a.title}</b><button className="btn" onClick={onClose} style={{ padding: "5px 11px" }}>{L("Close", "Mbyll")}</button></div>
      <div className="cp-body">
        {a.kind === "raci" && (
          <>
            <div className="sheet"><div className="sheet-bar"><Icon name="grid" size={14} /> {a.title}.xlsx</div>
              <div className="tbl-wrap"><table><thead><tr><th>{T("Task")}</th>{a.people.map((p) => <th key={p}>{p}</th>)}</tr></thead>
                <tbody>{a.rows.map((r) => <tr key={r.task}><td>{r.task}</td>{r.cells.map((v, i) => <td key={i}>{v ? <span className={`ra ${v}`}>{v}</span> : <span className="faint">·</span>}</td>)}</tr>)}</tbody></table></div></div>
            <p className="faint" style={{ fontSize: 12 }}>{T("R does the work · A owns the outcome (one per task) · C gives input · I is kept informed.")}</p>
          </>
        )}
        {a.kind === "timetable" && (
          <div className="sheet"><div className="sheet-bar"><Icon name="cal" size={14} /> {a.title}.xlsx</div>
            <div className="tbl-wrap"><div className="gantt" style={{ minWidth: 420 }}>
              <div className="wk" style={{ textAlign: "left" }}>{T("Task")}</div>{Array.from({ length: a.weeks }, (_, i) => <div className="wk" key={i}>{T("Wk")}{" "}{i + 1}</div>)}
              {a.rows.map((r) => [<div key={r.task}>{r.task}</div>, ...Array.from({ length: a.weeks }, (_, i) => {
                const w = i + 1;
                return <div className="cell" key={r.task + w}>{w >= r.start && w <= r.end && <span className={`seg ${r.milestone ? "ms" : ""}`} style={r.milestone ? undefined : { background: `linear-gradient(90deg, ${COLOR_BY_FIRST[r.owner] ?? "#8a55f7"}, #b996ff)` }} />}</div>;
              })])}
            </div></div></div>
        )}
        {a.kind === "matrix" && (
          <div className="sheet"><div className="sheet-bar"><Icon name="matrix" size={14} /> {a.title}.xlsx</div>
            <div style={{ padding: 10 }}><div className="quad">{a.quadrants.map((q) => (
              <div key={q.name}><h4 style={{ color: { "Do now": "var(--rose)", Schedule: "var(--lav)", Delegate: "var(--ok)", Drop: "var(--faint)" }[q.name] }}>{q.name}</h4><ul>{q.items.map((i) => <li key={i}>{i}</li>)}</ul></div>))}</div></div></div>
        )}
        {a.kind === "doc" && <div className="doc"><h4>{a.title}</h4><p>{a.body}</p></div>}
        <div className="row">
          {a.kind === "doc" && a.format === "Outlook" ? <button className="btn primary" onClick={() => void toDrafts(a.title, a.body)}>{T("Put in Outlook drafts")}</button> : <>
            <button className="btn primary" onClick={() => void downloadAs(a, a.kind === "doc" ? "docx" : "xlsx")}>{a.kind === "doc" ? L("Open Word", "Hap në Word") : L("Open Excel", "Hap në Excel")}</button>
            <button className="btn" onClick={() => void downloadAs(a, "pdf")}>{L("Open PDF", "Hap PDF")}</button>
            <button className="linkbtn" onClick={() => (a.kind === "doc" ? void downloadAs(a, "docx", "save") : onExcel(a))}>{T("Save")}{" "}{a.kind === "doc" ? "Word" : "Excel"}</button>
            <button className="linkbtn" onClick={() => void downloadAs(a, "pdf", "save")}>{L("Save PDF", "Ruaj PDF")}</button>
          </>}
          {a.kind !== "doc" && <button className="btn" onClick={onPlanner}>{T("Send to Planner")}</button>}
        </div>
      </div>
    </>
  );
}

function ReplyCard({ c }: { c: Card }) {
  if (c.type === "weather") {
    return (
      <div className="rcard">
        <div className="row" style={{ justifyContent: "space-between" }}><b><Icon name="sun" size={15} /> {c.place}</b><span className="faint">{c.summary}</span></div>
        <div className="wnow">{c.now}</div>
        <div className="wdays">{c.days.map((d) => <div key={d.day}><b>{d.day}</b><span>{d.high} / {d.low}</span><small>{d.sky}</small></div>)}</div>
      </div>
    );
  }
  const faster = c.usual - c.minutes;
  return (
    <div className="rcard">
      <b><Icon name="car" size={15} /> {c.route}</b>
      <div className="row" style={{ alignItems: "baseline", gap: 10 }}><span className="wnow">{c.minutes} min</span><span className={faster >= 0 ? "ok" : "bad"}>{faster >= 0 ? Tf("{n} min faster than usual", { n: faster }) : Tf("{n} min slower than usual", { n: -faster })}</span></div>
      <small className="faint">{c.note}</small>
    </div>
  );
}
